import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { useChangeApp } from '@/shared/hooks/useChangeApp';
import ComingSoonBadge from '@/features/home-v2/components/ui/atoms/ComingSoonBadge';
import ProductRowCard from '@/features/home-v2/components/ui/molecules/ProductRowCard';
import QuickSearchInput from '@/features/home-v2/components/ui/molecules/QuickSearchInput';
import {
  HomeGreetingV2,
  ProductSectionCard,
} from '@/features/home-v2/components/ui/organisms';
import {
  IDWALL_PRODUCTS,
  PDT_PRODUCTS,
  PORTAL_ID_PRODUCTS,
  PRODUCT_ICON_SIZE,
} from '@/features/home-v2/constants/products';
import type { HomeProduct } from '@/features/home-v2/types/products';
import './HomePage.css';

/**
 * HomePage
 *
 * Unified home page (Figma node `1601:13658`): greeting row plus three
 * platform columns — Plataforma Data Trust, Portal de Identidades Digitais and
 * Plataforma idwall (announced as "EM BREVE").
 *
 * ## Navigation flow
 *
 * ```mermaid
 * flowchart TD
 *     A[HomePage mounts] --> B[useUserInfo]
 *     B --> C{isLoading?}
 *     C -->|yes| D[Skeleton greeting]
 *     C -->|no| E[Greeting + platform columns]
 *     E --> F{Card activated}
 *     F -->|PDT product| G[useChangeApp → VITE_PDT_CLIENT_ID]
 *     F -->|Portal ID product| H[useChangeApp → VITE_PORTAL_ID_CLIENT_ID]
 *     F -->|idwall product| I[inert]
 * ```
 *
 * @returns Rendered unified home page
 *
 * @example
 * ```tsx
 * <Route index element={<HomePage />} />
 * ```
 *
 * @since 1.0.0
 * @category Pages
 */
export const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { data: userInfo, isLoading } = useUserInfo();
  const { mutate: navigateTo } = useChangeApp();

  const handleAccess = useCallback(
    (product: HomeProduct) => {
      if (!product.url) return;

      navigateTo({
        url: product.url,
        targetClientId:
          product.platform === 'portalId'
            ? import.meta.env.VITE_PORTAL_ID_CLIENT_ID
            : import.meta.env.VITE_PDT_CLIENT_ID,
      });
    },
    [navigateTo]
  );

  const renderProduct = useCallback(
    (product: HomeProduct) => {
      const { Icon } = product;

      return (
        <ProductRowCard
          key={product.key}
          title={t(product.titleKey)}
          description={t(product.descriptionKey)}
          disabled={product.disabled}
          icon={<Icon width={PRODUCT_ICON_SIZE} height={PRODUCT_ICON_SIZE} />}
          onAccess={() => handleAccess(product)}
        />
      );
    },
    [handleAccess, t]
  );

  return (
    <div
      className="home-page"
      aria-label={t('Home page')}
      data-testid="home-page"
    >
      <HomeGreetingV2 isLoading={isLoading} name={`${userInfo?.name ?? ''}`} />

      <div className="home-page__columns">
        <ProductSectionCard title={t('Data Trust Platform')}>
          <QuickSearchInput />
          {PDT_PRODUCTS.map(renderProduct)}
        </ProductSectionCard>

        <ProductSectionCard title={t('Digital Identities Portal')}>
          {PORTAL_ID_PRODUCTS.map(renderProduct)}
        </ProductSectionCard>

        <ProductSectionCard
          title={t('idwall Platform')}
          badge={<ComingSoonBadge />}
          muted
        >
          {IDWALL_PRODUCTS.map(renderProduct)}
        </ProductSectionCard>
      </div>
    </div>
  );
};

export default HomePage;
